import React, { useEffect, forwardRef } from 'react';
import clsx from 'clsx';
import { Theme, useTheme, Drawer, Text, useMediaQuery, Tooltip, Mark, Link } from '@maas-components/core';
import { ExpandLessOutlined, MenuOutlined, KeyboardArrowLeftOutlined } from '@maas-components/icons';
import shell from '../../../theme';
import { LinkEvent, Links, Menu, Options, SetOptions } from '../types';
import ToolBar from '../Toolbar';
import useStyles from './styles';
import isArray from 'lodash/isArray';
import size from 'lodash/size';

export interface SidebarDrawerProps {
    enableColorDark?: boolean;
    menu: Menu;
    version: string;
    options: Options;
    setOptions: SetOptions;
    paddingLeftOnOpen?: boolean;
    paddingTop?: number;
    NavLink?: React.ElementType;
    onClickMenuItem?: (event: LinkEvent, link: Links) => void;
}

const SidebarDrawer = (props: SidebarDrawerProps) => {
    const theme: Theme = useTheme();

    const { enableColorDark, menu, version, options, setOptions, NavLink, onClickMenuItem } = props;

    const mode = enableColorDark ? 'dark' : theme.palette.type;

    const { isOpen, isOpenFromMenu } = options;

    const isDownMD = useMediaQuery(() => theme.breakpoints.down(1540));

    const isDownSM = useMediaQuery(() => theme.breakpoints.down(1324));

    const classes = useStyles({ mode });

    useEffect(() => {
        if (!isOpenFromMenu) setOptions({ ...options, isOpen: !isDownMD });
    }, [isDownMD]);

    const handleToggleNavigation = () => {
        if (!isOpen) setOptions({ ...options, isOpen: !isOpen, isOpenFromMenu: true });
    };

    const handleCloseMenuManual = () => {
        setOptions({
            ...options,
            isOpen: !isOpen,
            isOpenFromMenu: true,
        });
    };

    const cssDrawer = clsx(classes.drawer, {
        [classes.drawerOpen]: isOpen,
        [classes.drawerClose]: !isOpen,
    });

    const cssPaper = clsx(classes.drawer, {
        [classes.drawerOpen]: isOpen,
        [classes.drawerClose]: !isOpen,
    });

    const TextToolTip = forwardRef(function TextToolTip(
        { text }: { text: string },
        ref: React.LegacyRef<HTMLDivElement>,
    ) {
        return (
            <div ref={ref} className={classes.menuLinkName}>
                {text}
            </div>
        );
    });

    const flutuante = useMediaQuery(() => theme.breakpoints.between(1324, 1540));

    let tipo: 'permanent' | 'persistent' | 'temporary' | undefined = 'permanent';

    if (isOpen && flutuante) tipo = undefined;

    if (!isOpen && flutuante) tipo = 'permanent';

    const handleCloseOnClick = () => {
        if (isDownMD) {
            setOptions({ ...options, isOpen: false });
        }
    };

    return (
        <Drawer
            classes={{ paper: cssPaper }}
            className={cssDrawer}
            elevation={3}
            onClick={handleToggleNavigation}
            open={isOpen}
            style={{ zIndex: 1000 }}
            variant={tipo}
            ModalProps={{
                hideBackdrop: true,
            }}
            PaperProps={{
                style: {
                    backgroundColor: shell[mode].sidebar.background,
                },
            }}
            SlideProps={{
                onEntering: (node) => {
                    node.style.transition = theme.transitions.create(
                        ['transform', 'margin', 'height', 'width', 'top', 'left'],
                        {
                            easing: theme.transitions.easing.easeInOut,
                            duration: '0.3s',
                        },
                    );
                },
            }}
        >
            {isOpen ? (
                <>
                    <div className={classes.menuContainer}>
                        <div onClick={handleCloseMenuManual}>
                            <div className={classes.menuIconClose}>
                                {isDownSM ? <ExpandLessOutlined /> : <KeyboardArrowLeftOutlined />}
                            </div>
                            <div className={classes.menuTitle}>
                                <div className={classes.menuIcon}>{menu.title.icon}</div>
                                <div className={classes.menuName}>
                                    <Text fontSize="small" color="inherit" className={classes.menuNameTruncate}>
                                        {menu.title.name}
                                    </Text>
                                </div>
                            </div>
                        </div>
                        <div>
                            {menu?.links &&
                                isArray(menu.links) &&
                                menu.links.map((link, index) => {
                                    const lastItem = index === size(menu?.links || []) - 1;
                                    const { name } = link;
                                    return (
                                        <Link
                                            key={`${name}-${index}}`}
                                            {...(!link?.href && { component: NavLink })}
                                            {...(link?.url && { to: link?.url })}
                                            {...link}
                                            color="textPrimary"
                                            className={classes.menuLink}
                                            onClick={(event) => {
                                                handleCloseOnClick();
                                                link?.onClick?.();
                                                onClickMenuItem?.(event, link);
                                            }}
                                        >
                                            <span className={classes.menuLinkBall} />
                                            {!lastItem && <div className={classes.menuLinkSymbol} />}
                                            <Tooltip title={name}>
                                                <TextToolTip text={name} />
                                            </Tooltip>
                                        </Link>
                                    );
                                })}
                        </div>
                    </div>
                    <div className={classes.version}>
                        <Text fontSize="xsmall" color="inherit">
                            Desenvolvido por Luizalabs
                        </Text>
                        <Text fontSize="xsmall" color="inherit">
                            Versão: <Mark>{version}</Mark>
                        </Text>
                    </div>
                </>
            ) : (
                <ToolBar
                    enableColorDark={enableColorDark}
                    title={menu.title.name}
                    icon={<MenuOutlined />}
                    options={options}
                    setOptions={setOptions}
                />
            )}
        </Drawer>
    );
};

export default SidebarDrawer;
